import { Badge, Button, Card, Group, Stack, Text, Title } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { ManualAbsence, StatusReportContact } from "@msc/shared";

import {
  useDeleteManualAbsence,
  useManualAbsences,
} from "../../hooks/useAbsences.js";
import { formatIsoDate, toLocalDateString } from "../../lib/date.js";
import { AbsenceModal } from "./AbsenceModal.js";

/**
 * Abwesenheiten der Ansprechpartner — in der Bearbeitung des Statusberichts.
 *
 * **Warum das hier steht und nicht nur in den Einstellungen.** Der Bericht sagt
 * dem Kunden, wann er uns nicht erreicht. Personio liefert dafür nur Urlaub und
 * Krankheit; ein Workshop, eine Fortbildung oder abgebaute Stunden stehen dort
 * gar nicht. Gemerkt wird das genau hier — beim Durchlesen des Berichts, kurz
 * vor dem Verschicken. Ein Weg über eine andere Seite und zurück wäre der
 * Moment, in dem es liegen bleibt.
 *
 * **Oben steht, was im Bericht landet, unten, was man davon anfassen kann.**
 * Die obere Liste kommt aus dem Bericht selbst (beide Quellen, verdichtet zu
 * Zeiträumen) — das ist, was der Kunde liest. Die untere sind die Hand-Einträge
 * mit ihrer Identität, nur die lassen sich ändern und löschen. Ein Urlaub aus
 * Personio wird in Personio geändert.
 */

/** Fenster der pflegbaren Einträge: 60 Tage zurück, 180 nach vorn. */
function fenster(): { from: string; to: string } {
  const von = new Date();
  von.setDate(von.getDate() - 60);
  const bis = new Date();
  bis.setDate(bis.getDate() + 180);
  return { from: toLocalDateString(von), to: toLocalDateString(bis) };
}

function KontaktZeile({ contact }: { contact: StatusReportContact }) {
  const { t } = useTranslation();
  const [{ from, to }] = useState(fenster);
  const manuell = useManualAbsences({ employeeId: contact.employeeId, from, to });
  const loeschen = useDeleteManualAbsence();
  const [offen, setOffen] = useState(false);
  const [bearbeitet, setBearbeitet] = useState<ManualAbsence | null>(null);

  const eintraege = manuell.data ?? [];

  return (
    <Stack gap={4}>
      <Group justify="space-between" align="center">
        <Group gap="xs">
          <Text fw={500} size="sm">
            {contact.fullName}
          </Text>
          <Badge variant="light" color="gray" size="sm">
            {t(
              contact.role === "lead"
                ? "pages.statusReports.absences.roleLead"
                : "pages.statusReports.absences.roleDeputy",
            )}
          </Badge>
        </Group>
        <Button
          size="xs"
          variant="light"
          // Der Name muss die PERSON nennen: Auf der Seite stehen zwei dieser
          // Knoepfe, einer je Ansprechpartner. Zwei Schaltflaechen mit
          // identischem Namen sind fuer eine Vorleseausgabe nicht
          // unterscheidbar.
          aria-label={t("pages.statusReports.absences.addFor", {
            name: contact.fullName,
          })}
          onClick={() => {
            setBearbeitet(null);
            setOffen(true);
          }}
        >
          {t("pages.statusReports.absences.add")}
        </Button>
      </Group>

      {/* Was im Bericht steht — beide Quellen. */}
      {contact.absences.length === 0 ? (
        <Text size="sm" c="dimmed">
          {t("pages.statusReports.absences.noneInReport")}
        </Text>
      ) : (
        <Text size="sm">
          {contact.absences
            .map((a) => `${formatIsoDate(a.dateFrom)} – ${formatIsoDate(a.dateTo)}`)
            .join(", ")}
        </Text>
      )}

      {/* Was davon von Hand erfasst ist — nur das ist hier änderbar. */}
      {eintraege.length > 0 ? (
        <Stack gap={2} mt={2}>
          {eintraege.map((eintrag) => (
            <Group key={eintrag.id} gap="xs">
              <Text size="xs" c="dimmed">
                {formatIsoDate(eintrag.dateFrom)} –{" "}
                {formatIsoDate(eintrag.dateTo)}
                {eintrag.dayFraction === 0.5
                  ? ` (${t("pages.settings.absences.halfDay")})`
                  : ""}
              </Text>
              {/* NICHT „Bearbeiten“: So heisst auf dieser Seite der Knopf,
                  der den Bericht selbst in den Bearbeiten-Modus schaltet. Zwei
                  Schaltflaechen gleichen Namens sind nicht nur fuer den
                  e2e-Test zweideutig, sondern auch fuer den Menschen, der sie
                  vorgelesen bekommt. Der Zeitraum steht im Namen, weil es je
                  Eintrag einen dieser Knoepfe gibt. */}
              <Button
                size="compact-xs"
                variant="subtle"
                aria-label={t("pages.statusReports.absences.changeAria", {
                  from: formatIsoDate(eintrag.dateFrom),
                  to: formatIsoDate(eintrag.dateTo),
                })}
                onClick={() => {
                  setBearbeitet(eintrag);
                  setOffen(true);
                }}
              >
                {t("pages.statusReports.absences.change")}
              </Button>
              <Button
                size="compact-xs"
                variant="subtle"
                color="red"
                loading={loeschen.isPending}
                aria-label={t("pages.statusReports.absences.removeAria", {
                  from: formatIsoDate(eintrag.dateFrom),
                  to: formatIsoDate(eintrag.dateTo),
                })}
                onClick={() => void loeschen.mutateAsync(eintrag.id)}
              >
                {t("pages.statusReports.absences.remove")}
              </Button>
            </Group>
          ))}
        </Stack>
      ) : null}

      {offen ? (
        <AbsenceModal
          key={bearbeitet?.id ?? "neu"}
          opened
          onClose={() => setOffen(false)}
          bearbeitet={bearbeitet}
          person={{ id: contact.employeeId, fullName: contact.fullName }}
        />
      ) : null}
    </Stack>
  );
}

export function AbsenceContactPanel({
  contacts,
}: {
  contacts: StatusReportContact[];
}) {
  const { t } = useTranslation();
  if (contacts.length === 0) return null;
  return (
    <Card withBorder padding="md">
      <Title order={4} mb={4}>
        {t("pages.statusReports.absences.title")}
      </Title>
      <Text c="dimmed" size="sm" mb="sm">
        {t("pages.statusReports.absences.hint")}
      </Text>
      <Stack gap="md">
        {contacts.map((c) => (
          <KontaktZeile key={c.employeeId} contact={c} />
        ))}
      </Stack>
    </Card>
  );
}
